import React, { useState } from 'react';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localeData from 'dayjs/plugin/localeData';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import weekYear from 'dayjs/plugin/weekYear';
import weekday from 'dayjs/plugin/weekday';
import { Users, Star, Target, Activity } from 'lucide-react';
import { ScrollArea } from '../../../../components/ui/scroll-area';
import useCardsData from '../hooks/useCardsData';
import ActivityChart from './components/ActivityChart';
import DateRangePicker from './components/DateRangePicker';
import LinkViewsCard from './components/LinkViewsCard';
import RecentReviews from './components/RecentReviews';
import StatsCard from './components/StatsCard';

dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);
dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(weekOfYear);
dayjs.extend(weekYear);

const CARD_CONFIGS = [
  {
    key: 'users',
    title: 'Total Users',
    icon: Users,
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/10',
  },
  {
    key: 'reviews',
    title: 'Total Reviews',
    icon: Star,
    color: 'text-amber-500',
    bgColor: 'bg-amber-500/10',
  },
  {
    key: 'goals',
    title: 'Total Goals',
    icon: Target,
    color: 'text-green-500',
    bgColor: 'bg-green-500/10',
  },
  {
    key: 'activities',
    title: 'Total Activities',
    icon: Activity,
    color: 'text-primary',
    bgColor: 'bg-primary/10',
  },
];

function HeadDashboardPageV3() {
  const [startPickerValue, setStartPickerValue] = useState(dayjs().subtract(1, 'month'));
  const [endPickerValue, setEndPickerValue] = useState(dayjs());
  const { cardsData } = useCardsData();

  const handleDateChange = (start, end) => {
    setStartPickerValue(start);
    setEndPickerValue(end);
  };

  // Map cardsData to our config
  const getCardValue = (key) => {
    const card = cardsData.find((c) => c.title.toLowerCase().includes(key));
    return card?.count ?? 0;
  };

  const isLoading = cardsData.length === 0;

  return (
    <ScrollArea className="h-full">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Dashboard</h1>
            <p className="text-muted-foreground mt-1">
              Overview of your department&apos;s performance
            </p>
          </div>
          <DateRangePicker
            startDate={startPickerValue}
            endDate={endPickerValue}
            onChange={handleDateChange}
          />
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {CARD_CONFIGS.map((config) => (
            <StatsCard
              key={config.key}
              title={config.title}
              value={getCardValue(config.key)}
              icon={config.icon}
              color={config.color}
              bgColor={config.bgColor}
              isLoading={isLoading}
            />
          ))}
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ActivityChart startPickerValue={startPickerValue} endPickerValue={endPickerValue} />
          <LinkViewsCard startPickerValue={startPickerValue} endPickerValue={endPickerValue} />
        </div>

        {/* Recent Reviews */}
        <RecentReviews startPickerValue={startPickerValue} endPickerValue={endPickerValue} />
      </div>
    </ScrollArea>
  );
}

export default HeadDashboardPageV3;
